import { cn } from "@/lib/utils";
import { cva, type VariantProps } from "class-variance-authority";

const spinnerVariants = cva("inline-flex items-center justify-center", {
  variants: {
    size: {
      sm: "h-5 w-5",
      md: "h-8 w-8",
      lg: "h-12 w-12",
      xl: "h-20 w-20",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

interface SpinnerProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof spinnerVariants> {}

export function Spinner({ size, className = "", ...props }: SpinnerProps) {
  const arcs = 5;

  return (
    <div
      className={cn(spinnerVariants({ size }), className)}
      role="status"
      aria-label="Loading"
      {...props}
    >
      <svg viewBox="0 0 50 50" className="h-full w-full" fill="none">
        {Array.from({ length: arcs }).map((_, i) => {
          const startAngle = i * (360 / arcs);
          const sweepAngle = 50;
          const r = 20;
          const cx = 25;
          const cy = 25;

          const toRad = (deg: number) => (deg * Math.PI) / 180;
          const x1 = cx + r * Math.cos(toRad(startAngle));
          const y1 = cy + r * Math.sin(toRad(startAngle));
          const x2 = cx + r * Math.cos(toRad(startAngle + sweepAngle));
          const y2 = cy + r * Math.sin(toRad(startAngle + sweepAngle));

          return (
            <path
              key={i}
              d={`M ${x1} ${y1} A ${r} ${r} 0 0 1 ${x2} ${y2}`}
              strokeWidth="4"
              strokeLinecap="round"
              className={cn(className, "stroke-primary")}
              style={{
                animation: `spinner-wave 1.4s ease-in-out ${i * 0.15}s infinite`,
              }}
            />
          );
        })}
      </svg>
    </div>
  );
}
